import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {getGrantsByIds} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {getPreset} from 'src/openapi/admin/apis/EntitlementsPresetApi';
import {getResourcesByIds} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {getRoleNamesByIds} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Plate} from '@pb/kit-component-plate';
import {pluralize} from '@pb/kit-formatters';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';


import {
    next,
    selectDrawerDetails,
    setPresetDetails,
} from '../entitlement-drawer.slice';
import {EntitlementType as EntitlementTypes} from '../typedef';
import {buildPresetDetails} from '../utils/buildPresetsDetails';

import {GrantList} from './components/grants-list';
import {RolesList} from './components/roles-list';
import {GrantSkeleton} from './grant.skeleton';

import {
    entitlementDetailsViewHierarchyItem,
    entitlementDetailsViewOffsetContainer,
    entitlementDetailsViewPlates,
    entitlementDetailsViewToggleLabel,
} from './entitlement-details-view.module.styl';


import type {EntitlementsResource} from 'src/openapi/admin/models/EntitlementsResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {PresetItem} from 'src/openapi/admin/models/PresetItem';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';
import type {Role} from 'src/openapi/admin/models/Role';

type Props = {
    presetId: string,
    relations: PresetRelationItem[],
};

export const PresetView = ({presetId, relations}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const {createAbortSignal} = useAbortSignal();

    const {presetDetails} = useSelector(selectDrawerDetails);
    const [rolesListExpanded, setRolesListExpanded] = useState(true);
    const [freeGrantsListExpanded, setFreeGrantsListExpanded] = useState(true);
    const [rolesExpanded, setRolesExpanded] = useState<string[]>([]);
    const [grantsExpanded, setGrantsExpanded] = useState<string[]>([]);

    useEffect(() => {
        const getPresetDetails = async () => {
            const signal = createAbortSignal();

            let preset: PresetItem = {
                id: '',
                name: '',
                description: '',
            };
            const presetsResponse = await apiDispatch(getPreset({presetId}, {signal}));
            if (presetsResponse.payload.success) {
                preset = presetsResponse.payload.data;
            } else {
                dispatch(setPresetDetails({error: presetsResponse.payload.data}));
                return;
            }


            let roles: Record<string, Role> = {};
            const roleIds = [...new Set(relations.filter(({roleId}) => roleId).map(({roleId}) => roleId || ''))];
            const rolesResponse = await apiDispatch(getRoleNamesByIds(
                {requestBody: roleIds},
                {signal},
            ));
            if (rolesResponse.payload.success) {
                roles = rolesResponse.payload.data;
            } else {
                dispatch(setPresetDetails({error: rolesResponse.payload.data}));
                return;
            }

            let grants: Record<string, Grant> = {};
            const grantIds = [...new Set(relations.map(({grantId}) => grantId))];
            const grantsResponse = await apiDispatch(getGrantsByIds(
                {requestBody: grantIds},
                {signal},
            ));
            if (grantsResponse.payload.success) {
                grants = grantsResponse.payload.data;
            } else {
                dispatch(setPresetDetails({error: grantsResponse.payload.data}));
                return;
            }

            let resources: Record<string, EntitlementsResource> = {};
            const resourceIds = [...new Set(relations.map(({resourceId}) => resourceId))];
            const resourcesResponse = await apiDispatch(getResourcesByIds(
                {requestBody: resourceIds},
                {signal},
            ));
            if (resourcesResponse.payload.success) {
                resources = Object.fromEntries(Object.entries(resourcesResponse.payload.data).map(
                    ([key, value]) => [key, {
                        name: value.name,
                        externalId: value.id,
                    }],
                ));
            } else {
                dispatch(setPresetDetails({error: resourcesResponse.payload.data}));
                return;
            }

            dispatch(setPresetDetails({
                data: buildPresetDetails({
                    preset,
                    relations: relations.filter(relation => relation.presetId === presetId),
                    roles,
                    grants,
                    resources,
                }),
            }));
        };

        getPresetDetails();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (presetDetails?.data?.roles) {
            setRolesExpanded(presetDetails.data.roles.map(({id}) => id));
            setGrantsExpanded([
                ...presetDetails.data.freeGrants.map(({id}) => id),
                ...presetDetails.data.roles.reduce((acc, {id: roleId, grants}) => [
                    ...acc,
                    ...grants.map(({id: grantId}) => `${roleId}-${grantId}`),
                ], [] as string[]),
            ]);
        }
    }, [presetDetails]);

    const checkRolesExpanded = useCallback((roleId: string) => {
        return rolesExpanded.includes(roleId);
    }, [rolesExpanded]);

    const checkFreeGrantsExpanded = useCallback((grantId: string) => {
        return grantsExpanded.includes(grantId);
    }, [grantsExpanded]);

    const toggleRolesList = useCallback(() => {
        setRolesListExpanded(prevState => !prevState);
    }, [setRolesListExpanded]);

    const toggleFreeGrantsList = useCallback(() => {
        setFreeGrantsListExpanded(prevState => !prevState);
    }, [setFreeGrantsListExpanded]);

    const toggleFreeGrants = useCallback((grantId: string) => {
        setGrantsExpanded(prevState => {
            if (prevState.includes(grantId)) {
                return prevState.filter(item => item !== grantId);
            } else {
                return [...prevState, grantId];
            }
        });
    }, [setGrantsExpanded]);

    const toggleRoles = useCallback((roleId: string) => {
        setRolesExpanded(prevState => {
            if (prevState.includes(roleId)) {
                return prevState.filter(item => item !== roleId);
            } else {
                return [...prevState, roleId];
            }
        });
    }, [setRolesExpanded]);

    const openChildResource = useCallback((entitlementId: string) => {
        dispatch(next({entitlementType: EntitlementTypes.RESOURCE, entitlementId}));
    }, [dispatch]);

    const openChildGrant = useCallback((entitlementId: string) => {
        dispatch(next({entitlementType: EntitlementTypes.GRANT, entitlementId}));
    }, [dispatch]);

    const openChildRole = useCallback((entitlementId: string) => {
        dispatch(next({entitlementType: EntitlementTypes.ROLE, entitlementId}));
    }, [dispatch]);

    const rolesCount = useMemo(() => (
        presetDetails?.data?.roles.length || 0
    ), [presetDetails]);

    const freeGrantsCount = useMemo(() => (
        presetDetails?.data?.freeGrants.length || 0
    ), [presetDetails]);

    const resourceCount = useMemo(() => {
        const uniqueIds = (presetDetails?.data?.freeGrants || []).reduce((acc, {resources}) => [
            ...new Set([...acc, ...resources.map(({externalId}) => externalId)]),
        ], [] as string[]);

        return uniqueIds.length;
    }, [presetDetails]);

    return (
        <Skeleton shown={!presetDetails} view={<GrantSkeleton/>}>
            <ServerResponseHandler serverError={presetDetails?.error || null} smallView={true}>
                {presetDetails?.data ? (
                    <Group direction="v" alignItems="start">
                        <div style={{width: 350}}>
                            <Label>Preset Description</Label>
                            {presetDetails.data.description}
                        </div>
                        {(rolesCount || freeGrantsCount) ? (
                            <Group alignItems="center">
                                {rolesCount ? (
                                    <div className={entitlementDetailsViewPlates}>
                                        <Plate
                                            value={rolesCount}
                                            caption={pluralize('Role[s] [is,are] in this preset', rolesCount)}
                                        />
                                    </div>
                                ) : null}
                                {freeGrantsCount ? (
                                    <div className={entitlementDetailsViewPlates}>
                                        <Plate
                                            value={freeGrantsCount}
                                            caption={pluralize('Grant[s] [is,are] in this preset', freeGrantsCount)}
                                        />
                                    </div>
                                ) : null}
                            </Group>
                        ) : null}
                        {resourceCount ? (
                            <Plate
                                value={resourceCount}
                                caption={
                                    pluralize('Resource[s] [is, are]', resourceCount) + ' in the ' +
                                    pluralize(`grant[s]`, freeGrantsCount) + ' of this preset'
                                }
                            />
                        ) : null}
                        {presetDetails.data.roles.length ? (
                            <div>
                                <div className={entitlementDetailsViewHierarchyItem} onClick={toggleRolesList}>
                                    <Icon type={rolesListExpanded ? Icons.ANGLE_UP : Icons.ANGLE_DOWN}/>
                                    <div className={entitlementDetailsViewToggleLabel}>
                                        <b>Roles</b>
                                    </div>
                                </div>
                                {rolesListExpanded ? (
                                    <div className={entitlementDetailsViewOffsetContainer}>
                                        <RolesList
                                            isExpanded={checkRolesExpanded}
                                            onToggleRole={toggleRoles}
                                            isGrantExpanded={checkFreeGrantsExpanded}
                                            onToggleGrant={toggleFreeGrants}
                                            roles={presetDetails.data.roles}
                                            onRoleClick={openChildRole}
                                            onGrantClick={openChildGrant}
                                            onResourceClick={openChildResource}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        {presetDetails.data.freeGrants.length ? (
                            <div>
                                <div className={entitlementDetailsViewHierarchyItem} onClick={toggleFreeGrantsList}>
                                    <Icon type={freeGrantsListExpanded ? Icons.ANGLE_UP : Icons.ANGLE_DOWN}/>
                                    <div className={entitlementDetailsViewToggleLabel}>
                                        <b>Grants</b>
                                    </div>
                                </div>
                                {freeGrantsListExpanded ? (
                                    <div className={entitlementDetailsViewOffsetContainer}>
                                        <GrantList
                                            isExpanded={checkFreeGrantsExpanded}
                                            onToggleGrant={toggleFreeGrants}
                                            grants={presetDetails.data.freeGrants}
                                            onResourceClick={openChildResource}
                                            onGrantClick={openChildGrant}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </Group>
                ) : <div/>}
            </ServerResponseHandler>
        </Skeleton>
    );
};
